import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Copy,
  EllipsisVertical,
  FolderSync,
  Hourglass,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  User,
  UserCheck,
  UserRoundX,
  Users as UsersIcon,
} from 'lucide-react'
import Avatar from '../../components/Avatar'
import { useRemembered } from '../../components/listMemory'
import useListParams from '../../components/useListParams'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import IconButton from '../../components/IconButton'
import { LabelChips } from '../../components/LabelChip'
import Menu, { MenuItem, MenuSeparator } from '../../components/Menu'
import PageHeader from '../../components/PageHeader'
import RejectedRowsModal from '../../components/RejectedRowsModal'
import Select from '../../components/Select'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import UserCloneModal from './UserCloneModal'
import UserCreateDrawer from './UserCreateDrawer'
import UserEditDrawer from './UserEditDrawer'
import UserImportModal from './UserImportModal'
import UserStatusModal from './UserStatusModal'
import {
  changePassword,
  createUser,
  deleteInactiveUsers,
  cloneUsersFromDirectory,
  deleteUser,
  getUsersPage,
  importUsers,
  listUsers,
  sortGroups,
  resetPassword,
  setUserApproved,
  setUserExpiry,
  setUserLocking,
  setUserResignation,
  setUserUnlocked,
  updateProfile,
  updateUserRoles,
} from './usersService'
import { strings as S } from './strings'
import './UsersPage.css'
import { serverDetail, serverText } from '../../lib/i18n'

/* The scopes ARE the page's KPI row, so they render as one — the same StatStrip
   the departments list uses, with the chosen scope held as the active filter.
   The state each scope names is the tone it wears: active is success, suspended
   is danger, waiting is warning, and the one scope counting a different kind of
   thing stays neutral. */
const TABS = [
  { id: 'ACTIVE', label: S.tabs.ACTIVE, icon: UserCheck, tone: 'success' },
  { id: 'SUSPENDED', label: S.tabs.SUSPENDED, icon: UserRoundX, tone: 'danger' },
  {
    id: 'WAITING_ACTIVATION_APPROVAL',
    label: S.tabs.WAITING_ACTIVATION_APPROVAL,
    icon: Hourglass,
    tone: 'warning',
  },
  { id: 'IDEAL_USERS', label: S.tabs.IDEAL_USERS, icon: UsersIcon, tone: 'neutral' },
]

const USER_TYPES = [
  { value: 'true', label: S.filter.adminsOnly },
  { value: 'false', label: S.filter.usersOnly },
]

const EMPTY_FILTER = { groupId: '', isAdmin: '' }

/**
 * What this list keeps in its own address — the scope, the page, the search and the two
 * filters. `/users?scope=SUSPENDED&page=4` IS page four of the suspended users: the entry
 * the browser goes back to already says so, a refresh keeps it, and it can be sent to
 * someone else. Defaults stay out of the URL.
 */
const LIST_PARAMS = { scope: 'ACTIVE', page: 1, size: 10, q: '', group: '', admin: '' }

/* `RejectedRowsModal` is shared with the departments screen and reads one flat object; the
   `#` column is the one heading this list already has a word for. */
const REJECTED_LABELS = {
  ...S.importRejected,
  indexHeader: S.columns.index,
}

/**
 * قائمة المستخدمين — Views/Users/Index.cshtml + js.cshtml.
 *
 * Rebuilt around three moves, with every legacy control preserved:
 *
 *   1. The status tabs decide *which list you are on*, so they sit under the page
 *      title as a scope switcher instead of inside the table's toolbar.
 *   2. Filtering is inline and visible. The legacy hides it in a `بحث` dropdown and
 *      gives no sign afterwards that the list is filtered; here the department and
 *      user-type selects sit in the control strip and every applied filter stays up
 *      as a chip you can remove on its own.
 *   3. One primary action (`إضافة`) leads the header; `تحديث` and the destructive
 *      `حذف` fold into an overflow beside it, and the exports become one menu in the
 *      control strip instead of a pair of buttons under the table.
 */
export default function UsersPage() {
  const toast = useToast()
  /* Where this list is — in the address, so Back returns to it whatever else has
     happened in between. See components/useListParams.js. */
  const [params, setParams] = useListParams(LIST_PARAMS)
  const tab = params.scope
  const setTab = useCallback((next) => setParams({ scope: next, page: 1 }), [setParams])
  const query = params.q
  const setQuery = useCallback((next) => setParams({ q: next, page: 1 }), [setParams])
  const filter = useMemo(
    () => ({ groupId: params.group, isAdmin: params.admin }),
    [params.group, params.admin],
  )
  const setFilter = useCallback(
    (next) => {
      const applied = typeof next === 'function' ? next({ groupId: params.group, isAdmin: params.admin }) : next
      setParams({ group: applied.groupId, admin: applied.isAdmin, page: 1 })
    },
    [params.group, params.admin, setParams],
  )
  const [groupOptions, setGroupOptions] = useState([])
  /* The roles this installation actually has, from the page payload. The edit
     drawer used to list a hard-coded set out of `src/data/directory`, which is a
     mock — a role renamed or removed server-side would have gone on being
     offered, and granting it writes a role name straight into Identity. */
  const [roleOptions, setRoleOptions] = useState([])
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  /* Bumped by `reload()` to re-run both list effects below. See the comment there. */
  const [reloadNonce, setReloadNonce] = useState(0)
  /* The list is paged, sorted and searched by the SERVER — a table of users is far too
     large to send at once — so this is the request state, not a view of local data. The
     page and the page size are the URL's; the sort is remembered for the tab, since a
     column order is not something anyone links to. */
  const [sort, setSort] = useRemembered('users:sort', undefined)
  const listState = useMemo(
    () => ({ page: params.page, pageSize: params.size, sort }),
    [params.page, params.size, sort],
  )
  const setListState = useCallback(
    (next) => {
      const applied = typeof next === 'function' ? next({ page: params.page, pageSize: params.size, sort }) : next
      setParams({ page: applied.page, size: applied.pageSize })
      setSort(applied.sort)
    },
    [params.page, params.size, sort, setParams, setSort],
  )
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one. Without this the table renders "no records found"
     for a database fault — see the `error` prop on DataTable. */
  const [loadError, setLoadError] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [importType, setImportType] = useState(null)
  /** The rows an import could not take — see `RejectedRowsModal`. */
  const [rejected, setRejected] = useState(null)
  const [cloneOpen, setCloneOpen] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [sync, setSync] = useState({ canClone: false, canFetch: false, sources: [] })
  const [editing, setEditing] = useState(null)
  const [changingStatus, setChangingStatus] = useState(null)
  const [deleting, setDeleting] = useState(null)

  // One call for both — the departments and the role list arrive in the same payload.
  useEffect(() => {
    getUsersPage().then((page) => {
      setGroupOptions(sortGroups(page.groups))
      setRoleOptions(page.roles)
      /* Both directory actions need a configured source, and the payload has said whether
         there is one since the slice was wired — read by nothing until now. */
      setSync({
        canClone: page.hasUsersDataSource,
        importSourceId: page.usersSourceId,
        canFetch: page.hasFetchDataSource,
        fetchSourceId: page.fetchDataSourceId,
        sources: page.dataSources,
      })
    })
  }, [])

  /* How big each scope is, so the switcher can say so on its own cards. Four
     calls to the SAME `listUsers` the list already uses — no new endpoint, no
     new field, and unfiltered on purpose: a scope's size is a property of the
     scope, not of whatever department filter happens to be applied. The
     invitations screen reads its strip the same way. */
  const [scopeCounts, setScopeCounts] = useState({})

  useEffect(() => {
    let cancelled = false
    /* `ALL` as well: the title's count. It showed the chosen card's size — 10, then 2 on
       "suspended", then 5 — so the page never said how many users it holds (Taiga #1518).
       Each card already carries its own figure, and the table footer the filtered one. */
    Promise.all(
      [...TABS.map((item) => item.id), 'ALL'].map((type) =>
        listUsers({ type }).then((response) => [type, response.total]),
      ),
    ).then((entries) => {
      if (!cancelled) setScopeCounts(Object.fromEntries(entries))
    })
    return () => {
      cancelled = true
    }
    /* Follows `reload()` too: deleting the inactive users empties that scope, and a card
       still offering "12 inactive" is the same stale read as the table underneath it. */
  }, [reloadNonce])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listUsers({
      type: tab,
      groupId: filter.groupId === '' ? null : Number(filter.groupId),
      isAdmin: filter.isAdmin === '' ? null : filter.isAdmin === 'true',
      page: listState.page,
      pageSize: listState.pageSize,
      search: query,
      sort: listState.sort,
    })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setTotal(response.total)
        setLoadError(null)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setLoadError(error)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tab, filter, query, listState, reloadNonce])

  // What is actually filtering the list right now, named with the labels the
  // legacy already uses for those selects.
  const chips = useMemo(() => {
    const applied = []
    if (filter.groupId !== '') {
      applied.push({
        id: 'groupId',
        label: S.filter.groupPlaceholder,
        value: groupOptions.find((option) => String(option.value) === String(filter.groupId))?.label ?? '',
        onRemove: () => setFilter((previous) => ({ ...previous, groupId: '' })),
      })
    }
    if (filter.isAdmin !== '') {
      applied.push({
        id: 'isAdmin',
        label: S.filter.typePlaceholder,
        value: USER_TYPES.find((option) => option.value === filter.isAdmin)?.label ?? '',
        onRemove: () => setFilter((previous) => ({ ...previous, isAdmin: '' })),
      })
    }
    return applied
  /* `setFilter` never changes; it is listed because it now arrives through
     `useRemembered`, which the linter cannot see through. */
  }, [filter, groupOptions, setFilter])

  const columns = useMemo(
    () => [
      /* The account name is a value in its own right — it is what an admin
         types into a support ticket and what an import file keys on — so it
         gets a column instead of riding under the display name as a caption.
         Sorting and searching a stacked pair only ever reached the outer one. */
      {
        key: 'name',
        header: S.columns.name,
        value: (row) => row.name,
        render: (row) => (
          <span className="users__identity">
            <Avatar name={row.name} size="sm" />
            <Link className="users__link" to={`/users/${row.id}`}>
              <bdi>{row.name}</bdi>
            </Link>
          </span>
        ),
      },
      {
        key: 'userName',
        header: S.columns.userName,
        render: (row) => (
          <span className="users__handle" dir="ltr">
            {row.userName}
          </span>
        ),
      },
      {
        key: 'email',
        header: S.columns.email,
        render: (row) => (
          <span className="users__mail" dir="ltr">
            {row.email}
          </span>
        ),
      },
      {
        key: 'groups',
        header: S.columns.groups,
        /* A user is in several; there is no one value to order by. */
        sortable: false,
        value: (row) => row.groupNames.join('، '),
        /* Every department carries its own tint, hashed from its name, so a user
           who belongs to four of them reads as four separable chips instead of
           four identical emerald ones — and the same department keeps the same
           colour down the whole column. */
        render: (row) => <LabelChips names={row.groupNames} />,
      },
      {
        key: 'status',
        header: S.columns.status,
        value: (row) => row.status.map((badge) => badge.label).join(' '),
        render: (row) => (
          <span className="users__chips">
            {row.status.map((badge) => (
              <StatusBadge key={badge.label} tone={badge.color}>
                {badge.label}
              </StatusBadge>
            ))}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'end',
        width: '104px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="data-table__row-actions">
            <IconButton icon={Pencil} label={S.row.edit} onClick={() => setEditing(row)} />
            <IconButton
              icon={Lock}
              /* It opened the STATUS dialog and was labelled "edit" — the same word as
                 the pencil beside it, so the two buttons said the same thing. */
              label={S.row.status}
              /* Suspending, locking, reinstating — the reversible negative, which is what
                 amber is for here. Red stays with the delete beside it. */
              tone="warning"
              onClick={() => setChangingStatus(row)}
            />
            {/* `/Users/DeleteUser` has been in the service since the slice was wired and
                had no control anywhere: there was NO WAY to delete a user in this app. */}
            <IconButton
              icon={Trash2}
              label={S.row.delete}
              tone="danger"
              onClick={() => setDeleting(row)}
            />
          </span>
        ),
      },
    ],
    [],
  )

  /**
   * Re-reads the current page, after something changed what is on it.
   *
   * A COUNTER, not a state change. This used to re-set `listState` to the page, size and
   * sort it already held; React discards a set to the same values, `listState` kept its
   * identity, and the effect below — which depends on that identity — never re-ran. Every
   * action toasted success over a list that still showed what it had just deleted, until
   * the page was refreshed by hand. Same nonce that `src/lib/useResource.js` uses.
   */
  const reload = useCallback(() => setReloadNonce((n) => n + 1), [])

  /**
   * One place that turns an action's outcome into what the user sees.
   *
   * Every one of these used to close its dialog and toast success unconditionally, so a
   * refused create or a rejected password read exactly like a successful one. Now the
   * dialog stays OPEN on failure with the server's own reason — which is the part worth
   * reading, since it names the duplicate username or the policy the password missed.
   */
  const run = async (call, onDone) => {
    const outcome = await call
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || S.fail.action)
      return false
    }
    toast.success(serverText(outcome.message) || S.actionDone)
    onDone?.()
    reload()
    return true
  }

  return (
    <>
      <PageHeader
        title={S.title}
        count={scopeCounts.ALL}
        actions={
          <>
            <Menu
              width="286px"
              trigger={({ toggle, open }) => (
                <Button onClick={toggle} aria-expanded={open}>
                  <Plus size={16} strokeWidth={2} />
                  {S.actions.add}
                </Button>
              )}
            >
              <MenuItem icon={User} onClick={() => setCreateOpen(true)}>
                {S.actions.createLocalUser}
              </MenuItem>
              <MenuItem icon={UsersIcon} onClick={() => setImportType('UsersByEmail')}>
                {S.actions.importFromExcelLocal}
              </MenuItem>
              <MenuItem icon={UsersIcon} onClick={() => setImportType('ActiveDirectoryUsers')}>
                {S.actions.importFromExcelAd}
              </MenuItem>
            </Menu>

            {/* Everything that is not the page's one primary job. The legacy spread
                these over two header menus and the table footer. */}
            <Menu
              width="320px"
              trigger={({ toggle, open, id }) => (
                <IconButton
                  icon={EllipsisVertical}
                  label={S.actions.update}
                  size="md"
                  filled
                  tone="brand"
                  onClick={toggle}
                  aria-haspopup="true"
                  aria-expanded={open}
                  aria-controls={id}
                />
              )}
            >
              {/* `سحب وتزامن البيانات` only renders when an integration data source
                  exists — `UsersIntegrationDataSourceExist` on the payload. This
                  installation has none, so the entry above stays hidden here. */}
              {sync.canClone && (
                <MenuItem icon={FolderSync} onClick={() => setCloneOpen(true)}>
                  {S.clone.title}
                </MenuItem>
              )}
              <MenuItem icon={Copy} onClick={() => setImportType('ResignedUsers')}>
                {S.actions.importResigned}
              </MenuItem>
              <MenuItem icon={RefreshCw} onClick={() => setImportType('ResetUsersPasswords')}>
                {S.actions.resetPasswords}
              </MenuItem>
              {/* Shown only on the `لم يستخدموا النظام` tab, as in the legacy footer. */}
              {tab === 'IDEAL_USERS' && (
                <>
                  <MenuSeparator />
                  {/* Nothing listed, nothing to delete. */}
                  <MenuItem icon={Trash2} danger disabled={rows.length === 0} onClick={() => setConfirmOpen(true)}>
                    {S.footer.deleteInactive}
                  </MenuItem>
                </>
              )}
            </Menu>
          </>
        }
      />

      {/* One scope is always chosen — a user is on a list, never on none — so
          selecting simply moves the choice rather than toggling it off. */}
      <StatStrip
        label={S.title}
        activeId={tab}
        onSelect={setTab}
        loading={Object.keys(scopeCounts).length === 0}
        items={TABS.map((item) => ({
          id: item.id,
          icon: item.icon,
          label: item.label,
          value: scopeCounts[item.id] ?? 0,
          tone: item.tone,
        }))}
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.actions.filter }}
        chips={chips}
        resetLabel={S.filter.cancel}
        onReset={() => setFilter(EMPTY_FILTER)}
      >
        <Select
          value={filter.groupId}
          onChange={(groupId) => setFilter((previous) => ({ ...previous, groupId }))}
          options={groupOptions}
          placeholder={S.filter.groupPlaceholder}
          aria-label={S.filter.groupPlaceholder}
        />
        <Select
          value={filter.isAdmin}
          onChange={(isAdmin) => setFilter((previous) => ({ ...previous, isAdmin }))}
          options={USER_TYPES}
          placeholder={S.filter.typePlaceholder}
          aria-label={S.filter.typePlaceholder}
        />
      </FilterBar>

      <DataTable
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        rowTo={(row) => `/users/${row.id}`}
        initialSort={{ key: 'name', dir: 'asc' }}
        /* Server-paged: `rows` is one page and `total` is what the endpoint matched, so
           the pager counts the whole list rather than what happens to be on screen. */
        server={{
          total,
          page: listState.page,
          pageSize: listState.pageSize,
          sort: listState.sort,
          onChange: ({ page, pageSize, sort }) =>
            setListState({ page, pageSize, sort }),
        }}
      />

      <UserCloneModal
        open={cloneOpen}
        sources={sync.sources}
        saving={cloning}
        onClose={() => setCloneOpen(false)}
        onSubmit={async (sourceId) => {
          setCloning(true)
          const outcome = await cloneUsersFromDirectory(sourceId)
          setCloning(false)
          // A partial sync may have committed valid accounts. Refresh the list even
          // when the summary reports skipped accounts, and retain its useful counts.
          reload()
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.directory)
          setCloneOpen(false)
          toast.success(serverDetail(outcome.message) || S.actionDone)
        }}
      />

      <UserCreateDrawer
        /* The directory fetch is offered only when the payload says a source exists;
           the groups are what a fetched department gets matched against. */
        sources={sync.canFetch ? sync.sources : []}
        defaultSourceId={sync.fetchSourceId}
        groups={groupOptions}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => run(createUser(body), () => setCreateOpen(false))}
      />

      {/* The reply is `window.parent.finishUpload(bool,'…')` as text/html — parsed by
          upload.js, never executed. Its message lists the rows the server could not take,
          so it is shown rather than replaced with a generic "uploaded". */}
      <UserImportModal
        sources={sync.sources}
        defaultSourceId={sync.importSourceId}
        open={!!importType}
        type={importType}
        onClose={() => setImportType(null)}
        onSubmit={async ({ file, fileType, sourceId }) => {
          /* The type names the report, and `importType` is cleared before it is read. */
          const subject = S.importTypes[fileType]?.title ?? ''
          const outcome = await importUsers({ file, fileType, sourceId })

          if (outcome.rejectedRows) {
            setImportType(null)
            setRejected({
              names: outcome.rejectedRows,
              subject,
              message: serverDetail(outcome.message),
            })
            /* The server called this a failure, but it saved every row it COULD read first,
               so the list behind is already out of date. */
            reload()
            return
          }

          // A capacity-limited import may have saved valid users even when the result is partial.
          reload()
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.action)
          setImportType(null)
          toast.success(serverDetail(outcome.message) || S.actionDone)
        }}
      />

      <RejectedRowsModal
        report={rejected}
        labels={REJECTED_LABELS}
        onClose={() => setRejected(null)}
      />

      <UserEditDrawer
        open={!!editing}
        user={editing}
        groups={groupOptions}
        roles={roleOptions}
        onClose={() => setEditing(null)}
        onSubmit={(action, values) => {
          if (action === 'passwordMismatch') {
            toast.error(S.edit.passwordMismatch)
            return
          }
          if (action === 'passwordFormat') {
            toast.error(S.create.errors.passwordFormat)
            return
          }

          const close = () => setEditing(null)

          if (action === 'profile') {
            run(updateProfile({ ...values, userName: editing.userName }), close)
            return
          }
          if (action === 'roles') {
            run(updateUserRoles(editing.userName, values), close)
            return
          }
          if (action === 'resetPassword') {
            // Sends the user a link; it does not set a password on their behalf.
            run(resetPassword(editing.userName))
            return
          }
          if (action === 'changePassword') {
            run(changePassword(editing.userName, values.next), close)
            return
          }
        }}
      />

      <UserStatusModal
        open={!!changingStatus}
        user={changingStatus}
        onClose={() => setChangingStatus(null)}
        onSubmit={(action, values) => {
          const username = changingStatus.userName
          const close = () => setChangingStatus(null)

          if (action === 'approve') {
            run(setUserApproved(username, values.approved, values.notes), close)
            return
          }
          if (action === 'expiry') {
            run(setUserExpiry(username, values.activeUntil), close)
            return
          }
          if (action === 'lock') {
            run(setUserLocking(username, values.lockoutEnd), close)
            return
          }
          if (action === 'unlock') {
            run(setUserUnlocked(username), close)
            return
          }
          if (action === 'resignation') {
            run(setUserResignation(username, values.resignation), close)
          }
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        /* The page's rows, counted — the wording read as "every account that never signed in"
           while only the rows on screen are sent. */
        message={S.deleteInactiveConfirm(rows.length)}
        confirmLabel={S.footer.deleteInactive}
        cancelLabel={S.close}
        /* The endpoint takes the list to delete, so this passes the usernames currently
           on screen. It cannot mean "every inactive user in the database" — nothing here
           has seen them. */
        onConfirm={() =>
          run(
            deleteInactiveUsers(rows.map((row) => row.userName)),
            () => setConfirmOpen(false),
          )
        }
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Deleting a user was not possible anywhere in this app until now: the endpoint and
          its service function have both been there since the slice was wired, with no
          control in front of them. The confirmation names the account and says the change
          cannot be undone, because `DeleteUserHandler` removes the identity user outright
          rather than marking it. */}
      <ConfirmDialog
        open={!!deleting}
        message={deleting ? S.row.deleteConfirm(deleting.userName) : ''}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={async () => {
          await run(deleteUser(deleting.userName))
          setDeleting(null)
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  )
}
